import { useQuery, useMutation, useQueryClient, queryOptions, mutationOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { AnswerRequest, Form } from "../dynamicForm";
import { useRegistrationVariant } from "../pages/RegistrationsGridPage/RegistrationVariantProvider";
import type { RegistrationVariant } from "../pages/RegistrationsGridPage/RegistrationVariantProvider";
import type { CustomHistoryEntry, HistoryEntry } from "../dynamicHistory/types";
import type { ConnectedTenantsHistoryValue } from "./registrations.types";
import type {
  AddInviteePayload,
  CreatedRegistration,
  CreateRegistrationPayload,
  RegistrationById,
  RegistrationsGridView,
  UpdateRegistrationPayload,
  UsersWithAccessResponse,
  StatusChangeRequest,
  TenantHierarchy,
  RegistrationStatus,
} from "./registrations.types";
import { api } from "./apiClient";
import type { User } from "./users.types";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";
import { prefetch } from "./prefetch";

const BASE_ENDPOINT = (variant: RegistrationVariant) =>
  `${FORMS_API_BASE_ENDPOINT}/${variant === "Controller" ? "registrations" : "processor-registrations"}`;

const TENANT_BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/tenant`;

const registrationKeys = {
  all: (variant: RegistrationVariant) => ["registrations", variant] as const,
  byId: (variant: RegistrationVariant, id: string) => [...registrationKeys.all(variant), id] as const,
  history: (variant: RegistrationVariant, id: string) => [...registrationKeys.byId(variant, id), "history"] as const,
  gridView: (variant: RegistrationVariant) => [...registrationKeys.all(variant), "gridview"] as const,
  usersWithAccess: (variant: RegistrationVariant, id: string) =>
    [...registrationKeys.byId(variant, id), "usersWithAccess"] as const,
  usersWithAccessCount: (variant: RegistrationVariant, id: string) =>
    [...registrationKeys.byId(variant, id), "usersWithAccessCount"] as const,
  responsibleCandidates: (variant: RegistrationVariant, id: string) =>
    [...registrationKeys.byId(variant, id), "responsibleCandidates"] as const,
  responsible: (variant: RegistrationVariant, id: string) =>
    [...registrationKeys.byId(variant, id), "responsible"] as const,
  form: (variant: RegistrationVariant) => [...registrationKeys.all(variant), "form"] as const,
  dpiaRecommendedForm: (variant: RegistrationVariant) =>
    [...registrationKeys.all(variant), "dpiaRecommendedForm"] as const,
  myTenantsHierarchyWithCreatePermission: () => ["registrations", "tenantHierarchy"] as const,
  customerLibrary: (variant: RegistrationVariant) => [...registrationKeys.all(variant), "customerLibrary"] as const,
};

/** Get Registration by id */

const registrationByIdQueryOptions = (id: string, variant: RegistrationVariant, options?: { enabled: boolean }) => {
  return queryOptions({
    queryKey: registrationKeys.byId(variant, id),
    throwOnError: true,
    queryFn: async () => api.get(`${BASE_ENDPOINT(variant)}/${id}`) as Promise<RegistrationById>,
    ...options,
  });
};
export const prefetchRegistrationById = (id: string, variant: RegistrationVariant) =>
  prefetch(registrationByIdQueryOptions(id, variant));

export const useGetRegistrationById = (id: string, variant: RegistrationVariant, options?: { enabled: boolean }) =>
  useQuery(registrationByIdQueryOptions(id, variant, options));

/** Get Registration Form */

const registrationFormQueryOptions = (variant: RegistrationVariant) => {
  return queryOptions({
    queryKey: registrationKeys.form(variant),
    throwOnError: true,
    queryFn: async () => api.get(`${BASE_ENDPOINT(variant)}/form`) as Promise<Form>,
  });
};
export const prefetchRegistrationForm = (variant: RegistrationVariant) =>
  prefetch(registrationFormQueryOptions(variant));
export const useGetRegistrationForm = (variant: RegistrationVariant) => useQuery(registrationFormQueryOptions(variant));

/** Add Invitee */
export const useAddInvitee = (registrationId: string) => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: (payload: AddInviteePayload) =>
      api.post(
        `${BASE_ENDPOINT(variant)}/${registrationId}/invitees/${payload.userId}`,
        payload
      ) as Promise<RegistrationById>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Delete Invitee */
export const useDeleteInvitee = (registrationId: string) => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: (userId: string) =>
      api.delete(`${BASE_ENDPOINT(variant)}/${registrationId}/invitees/${userId}`) as Promise<RegistrationById>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Set Responsible */
export const useSetResponsible = (registrationId: string) => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationKey: registrationKeys.responsible(variant, registrationId),
    mutationFn: (userId: string) =>
      api.post(`${BASE_ENDPOINT(variant)}/${registrationId}/responsible/${userId}`) as Promise<unknown>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Get my tenant hierarchy but only tenants with create permission */
export const myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions = () =>
  queryOptions({
    queryKey: registrationKeys.myTenantsHierarchyWithCreatePermission(),
    queryFn: () =>
      api.get(`${TENANT_BASE_ENDPOINT}/me/creatorTenantHierarchy`, { schema: z.custom<TenantHierarchy>() }),
    throwOnError: true,
  });

export const responsibleCandidatesQueryOptions = (variant: RegistrationVariant, registrationId: string) =>
  queryOptions({
    queryKey: registrationKeys.responsibleCandidates(variant, registrationId),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT(variant)}/${registrationId}/responsible-candidates`, {
        schema: z.object({ users: z.array(z.custom<User>()) }),
      }),
    throwOnError: true,
  });

export type SetConnectedTenantsPayload = { registrationId: string; tenantIds: Array<string>; isConfirm?: boolean };

export const setConnectedTenantsMutationOptions = (variant: RegistrationVariant) =>
  mutationOptions({
    mutationFn: ({ registrationId, tenantIds, isConfirm }: SetConnectedTenantsPayload) =>
      api.put(`${BASE_ENDPOINT(variant)}/${registrationId}/tenants`, { tenantIds, isConfirm }),
    onSuccess: () => invalidateAll(),
  });

/** Update Dpia Recommended Answers */
export const useUpdateDpiaRecommendedAnswers = () => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: ({ registrationId, answers }: { registrationId: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT(variant)}/${registrationId}/dpia-auto-pre-assessment/answers`, answers),
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Get Dpia Recommended Form */
const dpiaRecommendedFormQueryOptions = (variant: RegistrationVariant) => {
  return queryOptions({
    queryKey: registrationKeys.dpiaRecommendedForm(variant),
    queryFn: () => api.get(`${BASE_ENDPOINT(variant)}/form/dpia-auto-pre-assessment`) as Promise<Form>,
    throwOnError: true,
  });
};
export const prefetchDpiaRecommendedForm = (variant: RegistrationVariant) =>
  prefetch(dpiaRecommendedFormQueryOptions(variant));
export const useGetDpiaRecommendedForm = (variant: RegistrationVariant) =>
  useQuery(dpiaRecommendedFormQueryOptions(variant));

/** Create registration */
export const useCreateRegistration = () => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: (payload: CreateRegistrationPayload) =>
      api.post(BASE_ENDPOINT(variant), payload) as Promise<CreatedRegistration>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Update registration status */
export const useUpdateRegistrationStatus = (registrationId: string) => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: (payload: StatusChangeRequest) =>
      api.patch(`${BASE_ENDPOINT(variant)}/${registrationId}/status`, payload) as Promise<CreatedRegistration>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Copy registration */
export const copyRegistrationMutationOptions = (variant: RegistrationVariant, registrationId: string) =>
  mutationOptions({
    mutationFn: (payload: { name: string; tenantIds: Array<string>; isConfirm?: boolean }) =>
      api.post(`${BASE_ENDPOINT(variant)}/${registrationId}/copy`, payload, { schema: z.object({ id: z.string() }) }),
    onSuccess: () => {
      void invalidateAll();
    },
  });

/** Delete registration */
export const useDeleteRegistration = () => {
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: (registrationId: string) => api.delete(`${BASE_ENDPOINT(variant)}/${registrationId}`),
    onSuccess: (_, registrationId) => invalidateAll({ ignore: [registrationKeys.byId(variant, registrationId)] }),
  });
};

/** Update registration answers */
export const useUpdateRegistrationAnswers = () => {
  const updateRegistrationCache = useUpdateRegistrationCache();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: ({ registrationId, answers }: { registrationId: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT(variant)}/${registrationId}/answers`, answers) as Promise<RegistrationById>,
    onSuccess: updateRegistrationCache,
  });
};

/** Update registration cache */
const useUpdateRegistrationCache = () => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return (updatedRegistration: RegistrationById) => {
    /** Update registration by id  */
    queryClient.setQueryData(registrationKeys.byId(variant, updatedRegistration.id), updatedRegistration);

    /** Update registration grid view */
    queryClient.setQueryData(
      registrationKeys.gridView(variant),
      (registrationsGridView: RegistrationsGridView | undefined) => {
        if (!registrationsGridView) return undefined;
        return {
          ...registrationsGridView,
          registrations: registrationsGridView.registrations.map(gridViewRegistration =>
            updatedRegistration.id === gridViewRegistration.registrationId
              ? {
                  ...gridViewRegistration,
                  dpiaStatus: updatedRegistration.autoDPIAPreAssessment?.dpiaStatus,
                  ...updatedRegistration,
                }
              : gridViewRegistration
          ),
        } satisfies RegistrationsGridView;
      }
    );
  };
};

/** Update registration */
export const useUpdateRegistration = () => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: ({ registrationId, payload }: { registrationId: string; payload: UpdateRegistrationPayload }) =>
      api.patch(`${BASE_ENDPOINT(variant)}/${registrationId}`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Get registrations grid view */
const registrationsGridViewQueryOptions = (variant: RegistrationVariant) => {
  return queryOptions({
    queryKey: registrationKeys.gridView(variant),
    throwOnError: true,
    queryFn: async () => api.get(`${BASE_ENDPOINT(variant)}/gridview`) as Promise<RegistrationsGridView>,
  });
};

export const prefetchRegistrationsGridView = (variant: RegistrationVariant) =>
  prefetch(registrationsGridViewQueryOptions(variant));

export const useGetRegistrationsGridView = (variant: RegistrationVariant) =>
  useQuery(registrationsGridViewQueryOptions(variant));

export const useUpdateRegistrationsGridViewStringifiedFilters = () => {
  const queryClient = useQueryClient();
  const variant = useRegistrationVariant();

  return useMutation({
    mutationFn: (stringifiedFilters: string) =>
      api.put(`${BASE_ENDPOINT(variant)}/gridview/filters`, { stringifiedFilters }),
    onMutate: stringifiedFilters => {
      queryClient.setQueryData(
        registrationKeys.gridView(variant),
        (registrationsGridView: RegistrationsGridView | undefined) => {
          if (!registrationsGridView) return undefined;
          return { ...registrationsGridView, stringifiedFilters } satisfies RegistrationsGridView;
        }
      );
    },
  });
};

const registrationUsersWithAccessQueryOptions = (registrationId: string, variant: RegistrationVariant) => {
  return queryOptions({
    queryKey: registrationKeys.usersWithAccess(variant, registrationId),
    queryFn: async () =>
      api.get(`${BASE_ENDPOINT(variant)}/${registrationId}/users`) as Promise<UsersWithAccessResponse>,

    throwOnError: true,
  });
};

export const prefetchRegistrationUsersWithAccess = (registrationId: string, variant: RegistrationVariant) =>
  prefetch(registrationUsersWithAccessQueryOptions(registrationId, variant));

export const useGetRegistrationUsersWithAccess = (registrationId: string, variant: RegistrationVariant) =>
  useQuery(registrationUsersWithAccessQueryOptions(registrationId, variant));

const UsersWithAccessCountSchema = z.object({ numberOfUsersWithAccess: z.number() });

const registrationUsersWithAccessCountOptions = (registrationId: string, variant: RegistrationVariant) => {
  return queryOptions({
    queryKey: registrationKeys.usersWithAccessCount(variant, registrationId),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT(variant)}/${registrationId}/users-with-access-count`, {
        schema: UsersWithAccessCountSchema,
      }),
  });
};

export const prefetchRegistrationUsersWithAccessCount = (registrationId: string, variant: RegistrationVariant) =>
  prefetch(registrationUsersWithAccessCountOptions(registrationId, variant));

export const useGetRegistrationUsersWithAccessCount = (registrationId: string, variant: RegistrationVariant) =>
  useQuery(registrationUsersWithAccessCountOptions(registrationId, variant));

/** Get Registration History */

type GetRegistrationHistoryResponse = {
  history: HistoryEntry<
    | CustomHistoryEntry<"registrationStatus", RegistrationStatus["status"]>
    | CustomHistoryEntry<"registrationName", string>
    | CustomHistoryEntry<"registrationResponsible", { id: string; firstName: string; lastName: string; email: string }>
    | CustomHistoryEntry<"registrationConnectedTenant", ConnectedTenantsHistoryValue>
    | CustomHistoryEntry<"assistantAnswersConfirmed", number>
  >[];
};

const registrationHistoryQueryOptions = (id: string, variant: RegistrationVariant) => {
  return queryOptions({
    queryKey: registrationKeys.history(variant, id),
    queryFn: () => api.get(`${BASE_ENDPOINT(variant)}/${id}/history`) as Promise<GetRegistrationHistoryResponse>,
    throwOnError: true,
  });
};

export const useGetRegistrationHistory = (id: string, variant: RegistrationVariant) =>
  useQuery(registrationHistoryQueryOptions(id, variant));

export const prefetchRegistrationHistory = (id: string, variant: RegistrationVariant) =>
  prefetch(registrationHistoryQueryOptions(id, variant));

const CustomerLibraryProcessingSchema = z.object({
  id: z.string(),
  name: z.string(),
  purpose: z.string().optional(),
  isAddedToOrganisation: z.boolean(),
});
export type CustomerLibraryProcessing = z.infer<typeof CustomerLibraryProcessingSchema>;

export const customerLibraryQueryOptions = () =>
  queryOptions({
    queryKey: registrationKeys.customerLibrary("Controller"),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT("Controller")}/customer-shared/catalogue`, {
        schema: z.array(CustomerLibraryProcessingSchema),
      }),
    throwOnError: true,
  });

export const addCustomerLibraryProcessingToOrganisationMutationOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => api.post(`${BASE_ENDPOINT("Controller")}/${id}/organisation-usage`),
    onSuccess: () => invalidateAll(),
  });
